import { useId, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { QRCodeSVG } from "qrcode.react";
import { toast } from "sonner";

import {
  createMyReferral,
  getMyReferral,
  normalizeReferralCode,
  ReferralApiError,
  updateMyReferral,
  validateReferralCodeFormat,
} from "../lib/referralApi";
import type { MyReferralData } from "../lib/referralApi";
import type { MyReferral, ReferralCode, ReferralListItem } from "../api/models";
import { ReferralListItemStatus } from "../api/models";
import { isReferralViewEnabled, useFeatureFlags } from "../hooks/use_feature_flags";
import "./ReferralPage.css";

const REFERRAL_QUERY_KEY = ["referral", "me"];

function formatDate(value: string): string {
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? "—"
    : date.toLocaleDateString("pt-BR", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
}

/**
 * Campo de código de indicação, compartilhado pela criação e pela troca.
 *
 * A caixa alta entra enquanto o usuário digita porque é o que o backend
 * persiste — melhor ele já ver o valor exato que vai virar link.
 */
function ReferralCodeField({
  label,
  hint,
  value,
  error,
  disabled,
  onChange,
}: {
  label: string;
  hint: string;
  value: string;
  error: string | null;
  disabled: boolean;
  onChange: (value: string) => void;
}) {
  const inputId = useId();
  const helpId = useId();

  return (
    <div className="referral-code-field">
      <label className="referral-code-label" htmlFor={inputId}>
        {label}
      </label>
      <input
        id={inputId}
        className={`referral-code-input${
          error ? " referral-code-input--error" : ""
        }`}
        value={value}
        onChange={(event) => onChange(normalizeReferralCode(event.target.value))}
        disabled={disabled}
        maxLength={20}
        placeholder="MEUCODIGO"
        autoComplete="off"
        autoCapitalize="characters"
        spellCheck={false}
        aria-invalid={error ? true : undefined}
        aria-describedby={helpId}
      />
      <span
        id={helpId}
        className={`referral-code-help${
          error ? " referral-code-help--error" : ""
        }`}
      >
        {error ?? hint}
      </span>
    </div>
  );
}

function InviteeRow({ invitee }: { invitee: ReferralListItem }) {
  const isQualified = invitee.status === ReferralListItemStatus.QUALIFIED;

  return (
    <li className="referral-invitee">
      <div className="referral-invitee-main">
        <span className="referral-invitee-name">
          {invitee.name?.trim() || "Convidado"}
        </span>
        {/* O e-mail já vem mascarado pela API — por design. */}
        <span className="referral-invitee-email">{invitee.email}</span>
      </div>
      <div className="referral-invitee-side">
        <span
          className={`referral-badge ${
            isQualified ? "referral-badge--ok" : "referral-badge--pending"
          }`}
        >
          {isQualified ? "Qualificado" : "Aguardando 1º depósito"}
        </span>
        <span className="referral-invitee-date">
          {formatDate(invitee.createdAt)}
        </span>
      </div>
    </li>
  );
}

export default function ReferralPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [copied, setCopied] = useState(false);
  /** Código sugerido na criação. Vazio = deixa o servidor sortear. */
  const [suggestedCode, setSuggestedCode] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const [draftCode, setDraftCode] = useState("");
  /** Erro do campo de código, seja da validação local ou do 400/409 da API. */
  const [codeError, setCodeError] = useState<string | null>(null);

  // Declarada como function para poder ser usada nos callbacks das mutations
  // logo abaixo sem depender da ordem de inicialização.
  function closeEditor() {
    setIsEditing(false);
    setDraftCode("");
    setCodeError(null);
  }

  const flagsQuery = useFeatureFlags();
  const referralQuery = useQuery<MyReferral>({
    queryKey: REFERRAL_QUERY_KEY,
    queryFn: getMyReferral,
    retry: false,
  });

  const createMutation = useMutation<ReferralCode, Error, string | undefined>({
    mutationFn: createMyReferral,
    onSuccess: () => {
      // O POST devolve só o código; totais e lista vêm do GET /referrals/me.
      void queryClient.invalidateQueries({ queryKey: REFERRAL_QUERY_KEY });
      setSuggestedCode("");
      setCodeError(null);
      toast.success("Seu link de indicação está pronto!");
    },
    onError: (error) => {
      if (error instanceof ReferralApiError) {
        // Os dois 409 da rota são coisas diferentes: o usuário já ter um código
        // não é erro (basta recarregar), mas o código escolhido estar tomado é.
        if (error.code === "E_REFERRAL_CODE_ALREADY_EXISTS") {
          void queryClient.invalidateQueries({ queryKey: REFERRAL_QUERY_KEY });
          return;
        }

        if (error.isAlreadyExists || error.isValidation) {
          setCodeError(error.message);
          return;
        }
      }

      toast.error(
        error instanceof ReferralApiError
          ? error.message
          : "Não foi possível gerar seu link.",
      );
    },
  });

  const updateMutation = useMutation<ReferralCode, Error, string>({
    mutationFn: updateMyReferral,
    onSuccess: (updated) => {
      void queryClient.invalidateQueries({ queryKey: REFERRAL_QUERY_KEY });
      closeEditor();
      toast.success(`Seu código agora é ${updated.code}.`);
    },
    onError: (error) => {
      if (error instanceof ReferralApiError) {
        // 409 aqui só pode ser código em uso; 400, formato ou termo reservado.
        if (error.isAlreadyExists || error.isValidation) {
          setCodeError(error.message);
          return;
        }

        // 404: o código sumiu entre o carregamento e o envio — recarrega a tela
        // em vez de insistir num formulário que não tem mais o que editar.
        if (error.isNotFound) {
          void queryClient.invalidateQueries({ queryKey: REFERRAL_QUERY_KEY });
          closeEditor();
        }
      }

      toast.error(
        error instanceof ReferralApiError
          ? error.message
          : "Não foi possível alterar seu código.",
      );
    },
  });

  const referral: MyReferralData | null = referralQuery.data ?? null;
  const flagEnabled = isReferralViewEnabled(flagsQuery.data);

  /** Cria o código, com ou sem sugestão. Só valida o formato quando há uma. */
  const handleCreate = () => {
    if (!suggestedCode) {
      setCodeError(null);
      createMutation.mutate(undefined);
      return;
    }

    const formatError = validateReferralCodeFormat(suggestedCode);
    if (formatError) {
      setCodeError(formatError);
      return;
    }

    setCodeError(null);
    createMutation.mutate(normalizeReferralCode(suggestedCode));
  };

  const handleUpdate = () => {
    const formatError = validateReferralCodeFormat(draftCode);
    if (formatError) {
      setCodeError(formatError);
      return;
    }

    if (referral && draftCode === referral.code) {
      closeEditor();
      return;
    }

    setCodeError(null);
    updateMutation.mutate(normalizeReferralCode(draftCode));
  };

  const handleCopy = async () => {
    if (!referral) return;

    try {
      await navigator.clipboard.writeText(referral.link);
      setCopied(true);
      toast.success("Link copiado!");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Não foi possível copiar. Copie manualmente o link acima.");
    }
  };

  const handleShare = async () => {
    if (!referral) return;

    if (!navigator.share) {
      await handleCopy();
      return;
    }

    try {
      await navigator.share({
        title: "wedofi",
        text: "Crie sua conta no wedofi com meu convite:",
        url: referral.link,
      });
    } catch {
      // Compartilhamento cancelado pelo usuário — nada a fazer.
    }
  };

  return (
    <div className="referral-page">
      <div className="referral-wrap">
        <header className="referral-header">
          <button
            type="button"
            className="referral-back"
            onClick={() => navigate("/profile")}
          >
            ← Voltar
          </button>
          <h1 className="referral-title">Indique e ganhe</h1>
          <p className="referral-subtitle">
            Compartilhe seu link. Quem entrar por ele fica vinculado a você
            automaticamente.
          </p>
        </header>

        {flagsQuery.isSuccess && !flagEnabled && (
          <p className="referral-flag-warning">
            A feature flag <code>account.referral.view</code>{" "}
            está desativada para esta conta — na produção esta seção fica
            escondida até a liberação.
          </p>
        )}

        {referralQuery.isPending && (
          <div className="referral-card referral-card--loading" aria-busy="true">
            <div className="referral-skeleton referral-skeleton--line" />
            <div className="referral-skeleton referral-skeleton--block" />
          </div>
        )}

        {referralQuery.isError && (
          <div className="referral-card referral-card--error">
            <p>
              {referralQuery.error instanceof ReferralApiError
                ? referralQuery.error.message
                : "Não foi possível carregar seus dados de indicação."}
            </p>
            <button
              type="button"
              className="referral-secondary-btn"
              onClick={() => void referralQuery.refetch()}
              disabled={referralQuery.isFetching}
            >
              {referralQuery.isFetching ? "Tentando..." : "Tentar novamente"}
            </button>
          </div>
        )}

        {referralQuery.isSuccess && !referral && (
          <div className="referral-card referral-card--empty">
            <div className="referral-emoji" aria-hidden="true">🎁</div>
            <h2 className="referral-card-title">
              Você ainda não tem um link
            </h2>
            <p className="referral-card-text">
              Gere seu código de indicação e convide quem você quiser. Cada
              pessoa que criar a conta pelo seu link entra na sua lista.
            </p>

            <ReferralCodeField
              label="Escolha seu código (opcional)"
              hint="4 a 20 caracteres: letras, números e hífen. Deixe vazio para sortearmos um."
              value={suggestedCode}
              error={codeError}
              disabled={createMutation.isPending}
              onChange={(value) => {
                setSuggestedCode(value);
                setCodeError(null);
              }}
            />

            <button
              type="button"
              className="referral-primary-btn"
              onClick={handleCreate}
              disabled={createMutation.isPending}
            >
              {createMutation.isPending
                ? "Gerando..."
                : suggestedCode
                ? "Gerar com esse código"
                : "Gerar meu link"}
            </button>
          </div>
        )}

        {referral && (
          <>
            <div className="referral-card">
              <span className="referral-card-label">Seu link de indicação</span>
              <div className="referral-link-row">
                <span className="referral-link">{referral.link}</span>
              </div>

              <div className="referral-link-actions">
                <button
                  type="button"
                  className="referral-primary-btn"
                  onClick={() => void handleCopy()}
                >
                  {copied ? "✅ Copiado!" : "📋 Copiar link"}
                </button>
                <button
                  type="button"
                  className="referral-secondary-btn"
                  onClick={() => void handleShare()}
                >
                  📤 Compartilhar
                </button>
              </div>

              <div className="referral-meta">
                <span>
                  Código <strong>{referral.code}</strong>
                </span>
                <span>Criado em {formatDate(referral.createdAt)}</span>
                {!referral.enabled && (
                  <span className="referral-badge referral-badge--off">
                    Desativado
                  </span>
                )}
                {!isEditing && (
                  <button
                    type="button"
                    className="referral-inline-btn"
                    onClick={() => {
                      setDraftCode(referral.code);
                      setCodeError(null);
                      setIsEditing(true);
                    }}
                  >
                    ✏️ Alterar código
                  </button>
                )}
              </div>

              {isEditing && (
                <div className="referral-code-editor">
                  <ReferralCodeField
                    label="Novo código"
                    hint="4 a 20 caracteres: letras, números e hífen."
                    value={draftCode}
                    error={codeError}
                    disabled={updateMutation.isPending}
                    onChange={(value) => {
                      setDraftCode(value);
                      setCodeError(null);
                    }}
                  />

                  <p className="referral-code-warning">
                    ⚠️ O link muda junto: quem já recebeu o link com{" "}
                    <strong>{referral.code}</strong>{" "}
                    não vai mais conseguir usá-lo. Suas{" "}
                    {referral.totalReferrals}{" "}
                    indicações já registradas são preservadas.
                  </p>

                  <div className="referral-link-actions">
                    <button
                      type="button"
                      className="referral-primary-btn"
                      onClick={handleUpdate}
                      disabled={updateMutation.isPending}
                    >
                      {updateMutation.isPending ? "Salvando..." : "Salvar código"}
                    </button>
                    <button
                      type="button"
                      className="referral-secondary-btn"
                      onClick={closeEditor}
                      disabled={updateMutation.isPending}
                    >
                      Cancelar
                    </button>
                  </div>
                </div>
              )}

              <div className="referral-qr">
                <QRCodeSVG value={referral.link} size={148} level="M" marginSize={2} />
                <span className="referral-qr-hint">
                  Aponte a câmera para abrir o convite
                </span>
              </div>
            </div>

            <div className="referral-stats">
              <div className="referral-stat">
                <span className="referral-stat-value">
                  {referral.totalReferrals}
                </span>
                <span className="referral-stat-label">Indicações</span>
              </div>
              <div className="referral-stat">
                <span className="referral-stat-value referral-stat-value--ok">
                  {referral.qualifiedReferrals}
                </span>
                <span className="referral-stat-label">Qualificadas</span>
              </div>
            </div>

            <div className="referral-card">
              <span className="referral-card-label">Quem entrou pelo link</span>

              {referral.referrals.length === 0
                ? (
                  <p className="referral-card-text">
                    Ninguém entrou ainda. Compartilhe seu link para começar.
                  </p>
                )
                : (
                  <>
                    <ul className="referral-invitees">
                      {referral.referrals.map((invitee, index) => (
                        <InviteeRow
                          key={`${invitee.email}-${index}`}
                          invitee={invitee}
                        />
                      ))}
                    </ul>
                    {referral.totalReferrals > referral.referrals.length && (
                      <p className="referral-footnote">
                        Mostrando as {referral.referrals.length}{" "}
                        indicações mais recentes de {referral.totalReferrals}.
                      </p>
                    )}
                  </>
                )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
